/**
 * Unified per-modality pricing — LLM, TTS and lipsync.
 *
 * ONE data table per modality (`LLM_RATES` / `TTS_RATES`) drives two views:
 *   1. `llmModelPricing` / `ttsModelPricing` — `BaseModelPricing<P>` entries, the modern shape
 *      that the unified resolver and the pricing tooling read.
 *   2. `llmEngineMeta` / `ttsEngineMeta` — the legacy `EngineMeta` shape `MODEL_METADATA` is
 *      assembled from, so existing callers (`quoteAllChannels`, admin UI) keep working untouched.
 *
 * That is what lets the old registry hold no data of its own: it is now a thin derived view, not
 * a second source of truth. Change a rate HERE and both views move together.
 *
 * Rates were generated from the values previously literal in `MODEL_METADATA`, so they are
 * numerically identical to what they replaced; `pricingSnapshot.test.ts` enforces that.
 */

import { z } from "zod"

import {
  ELipsyncModel,
  GoogleAudioModelType,
  InworldAudioModelType,
  MiniMaxAudioModelType,
  QwenAudioModelType,
  ResembleAIAudioModelType,
  SunoAudioGenerationModelType,
} from "../image/enums"
import {
  AwsPollyModelType,
  ChatGPTModelType,
  ChatGPTReasoningModelType,
  ClaudeModelType,
  ElevenLabsModelType,
  GeminiModelType,
  GroqModelType,
  OpenAIImageGenerationModelType,
  OpenRouterModelType,
} from "../llms/enums"
import { EPricingUnit } from "./replicate/pricing"
import type {
  LipsyncModelPricing,
  LipsyncPricingParams,
  LlmModelPricing,
  LlmPricingParams,
  MusicModelPricing,
  MusicPricingParams,
  TtsModelPricing,
  TtsPricingParams,
} from "./replicate/pricing"

/** Per-token legs for one text model. `cached` is undefined when the provider has no cache tier. */
interface LlmRate {
  input: number
  output: number
  cached: number | undefined
  name: string
  /**
   * Excluded from the `EngineMeta` view because `pricing.ts` defines it literally with a richer
   * shape. It still gets an LLM pricing entry so token-based callers can resolve it.
   */
  legacyOnlyInPricingTs?: boolean
}

/**
 * Per-CHARACTER TTS rate. Models that bill per OUTPUT instead (Suno music generation, which is
 * `modelType: TTS` but `unit: "quantity"`) are deliberately NOT in this table — a per-character
 * shape would price them at $0. They stay literal in `pricing.ts`.
 */
interface TtsRate {
  perCharacter: number
  name: string
  /**
   * Host that actually bills this model, plus the per-character rate IT charges. Populated from
   * a live validation sweep (2026-07-31) where every provider-reported cost matched the
   * configured `perCharacter` exactly, so provider and consumer rate are the same number today.
   * Recorded separately because they are free to diverge, and the model audit requires a
   * provider-cost block per case.
   */
  provider?: string
}

/** THE data. Both the modern and legacy views below are derived from these two tables. */
const LLM_RATES: Record<string, LlmRate> = {
  [ClaudeModelType.HAIKU_45]: {
    input: 0.000001,
    output: 0.000005,
    cached: 1.0000000000000001e-7,
    name: "Claude 4.5 Haiku",
  },
  [ClaudeModelType.OPUS_41]: { input: 0.000015, output: 0.000075, cached: 0.0000015, name: "Claude 4.1 Opus" },
  [ClaudeModelType.OPUS_45]: { input: 0.000015, output: 0.000075, cached: 0.0000015, name: "Claude 4.5 Opus" },
  [ClaudeModelType.OPUS_46]: { input: 0.000015, output: 0.000075, cached: 0.0000015, name: "Claude 4.6 Opus" },
  [ClaudeModelType.OPUS_48]: { input: 0.000015, output: 0.000075, cached: 0.0000015, name: "Claude 4.8 Opus" },
  [ClaudeModelType.SONNET_45]: { input: 0.000003, output: 0.000015, cached: 3e-7, name: "Claude 4.5 Sonnet" },
  [ClaudeModelType.SONNET_46]: { input: 0.000003, output: 0.000015, cached: 3e-7, name: "Claude 4.6 Sonnet" },
  // $2/M input, $10/M output, $0.20/M cache read: https://www.anthropic.com/news/claude-sonnet-5
  [ClaudeModelType.SONNET_5]: { input: 0.000002, output: 0.00001, cached: 2e-7, name: "Claude 5 Sonnet" },
  // Same rates as Sonnet 5: https://platform.claude.com/docs/en/about-claude/pricing
  [ClaudeModelType.SONNET_5_5]: { input: 0.000002, output: 0.00001, cached: 2e-7, name: "Claude 5.5 Sonnet" },
  // Base rates (prompts <=100K); >100K costs 5x, not modeled by this flat-rate registry.
  // https://platform.claude.com/docs/en/about-claude/pricing
  [ClaudeModelType.HAIKU_5_5]: { input: 1e-7, output: 5e-7, cached: 1e-8, name: "Claude 5.5 Haiku" },
  [OpenRouterModelType.DEEPSEEK_CHAT_V3]: {
    input: 1.4e-7,
    output: 2.8e-7,
    cached: undefined,
    name: "DeepSeek Chat V3",
  },
  [OpenRouterModelType.DEEPSEEK_R1]: {
    input: 5.5e-7,
    output: 0.0000021899999999999998,
    cached: undefined,
    name: "DeepSeek R1",
  },
  [OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B]: {
    input: 1.4e-7,
    output: 2.8e-7,
    cached: undefined,
    name: "DeepSeek R1 Distill Llama 70B",
  },
  [OpenRouterModelType.DEEPSEEK_V4_FLASH]: {
    input: 9e-8,
    output: 1.8e-7,
    cached: undefined,
    name: "DeepSeek V4 Flash",
  },
  [OpenRouterModelType.DEEPSEEK_V4_PRO]: { input: 4.35e-7, output: 8.7e-7, cached: 3.625e-9, name: "DeepSeek V4 Pro" },
  [OpenRouterModelType.GPT_6_LUNA]: { input: 1e-7, output: 5e-7, cached: undefined, name: "GPT-6 Luna" },
  [OpenRouterModelType.DEEPSEEK_V4_PRO_0813]: {
    input: 6.6e-7,
    output: 1.98e-6,
    cached: 2.2e-8,
    name: "DeepSeek V4 Pro (0813)",
  },
  [OpenRouterModelType.DEEPSEEK_V4_FLASH_0731]: {
    input: 1.08e-8,
    output: 1.28e-6,
    cached: 1.08e-8,
    name: "DeepSeek V4 Flash (0731)",
  },
  [OpenRouterModelType.GLM_5_3]: { input: 2.219e-7, output: 3.39e-6, cached: 1.775e-7, name: "GLM 5.3" },
  [OpenRouterModelType.QWEN_3_8_27B]: { input: 4.2e-7, output: 3e-6, cached: 8.5e-8, name: "Qwen 3.8 27B" },
  [GeminiModelType.GEMINI_2_5_FLASH]: {
    input: 8.000000000000001e-7,
    output: 0.0000024,
    cached: undefined,
    name: "Gemini 2.5 Flash",
  },
  [GeminiModelType.GEMINI_2_5_FLASH_LITE]: {
    input: 2.0000000000000002e-7,
    output: 6e-7,
    cached: undefined,
    name: "Gemini 2.5 Flash-Lite",
  },
  [GeminiModelType.GEMINI_3_FLASH_PREVIEW]: {
    input: 5e-7,
    output: 0.000003,
    cached: 5.0000000000000004e-8,
    name: "Gemini 3 Flash Preview",
  },
  [GeminiModelType.GEMINI_3_1_FLASH_IMAGE_PREVIEW]: {
    input: 5e-7,
    output: 0.000003,
    cached: undefined,
    name: "Gemini 3.1 Flash Image Preview",
  },
  [GeminiModelType.GEMINI_3_1_FLASH_LITE]: {
    input: 2.5e-7,
    output: 0.0000015,
    cached: 2.5000000000000002e-8,
    name: "Gemini 3.1 Flash-Lite",
  },
  [GeminiModelType.GEMINI_3_1_PRO_PREVIEW]: {
    input: 0.000002,
    output: 0.000012,
    cached: 2.0000000000000002e-7,
    name: "Gemini 3.1 Pro Preview",
  },
  // Launch pricing through 2026-12-31; list price from 2027-01-01 is input 1.5e-6, output 7.5e-6, cached 1.5e-7.
  [GeminiModelType.GEMINI_3_8_FLASH]: { input: 7.5e-7, output: 0.00000375, cached: 7.5e-8, name: "Gemini 3.8 Flash" },
  [GeminiModelType.GEMINI_3_5_FLASH]: { input: 0.0000015, output: 0.000009, cached: 1.5e-7, name: "Gemini 3.5 Flash" },
  [ChatGPTModelType.GPT_4_1]: { input: 0.000002, output: 0.000008, cached: 5e-7, name: "GPT-4.1" },
  [ChatGPTModelType.GPT_4_1_mini]: {
    input: 8.000000000000001e-7,
    output: 0.0000032000000000000003,
    cached: 2.0000000000000002e-7,
    name: "GPT-4.1 Mini",
  },
  [ChatGPTModelType.GPT_4_1_NANO]: {
    input: 2.0000000000000002e-7,
    output: 8.000000000000001e-7,
    cached: 5.0000000000000004e-8,
    name: "GPT-4.1 Nano",
  },
  [ChatGPTModelType.GPT_4o]: { input: 0.0000025, output: 0.00001, cached: undefined, name: "GPT-4o" },
  [ChatGPTModelType.GPT_4o_mini]: { input: 1.5e-7, output: 6e-7, cached: undefined, name: "GPT-4o Mini" },
  [ChatGPTModelType.GPT_5]: { input: 0.00000125, output: 0.00001, cached: 1.25e-7, name: "GPT-5" },
  [ChatGPTModelType.GPT_5_mini]: { input: 2.5e-7, output: 0.000002, cached: 2.5000000000000002e-8, name: "GPT-5 Mini" },
  [ChatGPTModelType.GPT_5_NANO]: { input: 5.0000000000000004e-8, output: 0.0000014, cached: 5e-9, name: "GPT-5 Nano" },
  [ChatGPTModelType.GPT_5_1]: { input: 0.00000125, output: 0.00001, cached: 1.25e-7, name: "GPT-5.1" },
  [ChatGPTModelType.GPT_5_2]: { input: 0.00000175, output: 0.000014, cached: 1.75e-7, name: "GPT-5.2" },
  [ChatGPTModelType.GPT_5_4]: { input: 0.0000025, output: 0.000015, cached: 2.5e-7, name: "GPT-5.4" },
  [ChatGPTModelType.GPT_5_4_mini]: { input: 7.5e-7, output: 0.0000045, cached: 7.5e-8, name: "GPT-5.4 Mini" },
  [ChatGPTModelType.GPT_5_4_NANO]: {
    input: 2.0000000000000002e-7,
    output: 0.00000125,
    cached: 2e-8,
    name: "GPT-5.4 Nano",
  },
  [ChatGPTModelType.GPT_5_5]: { input: 0.000005, output: 0.00003, cached: 5e-7, name: "GPT-5.5" },
  // GPT Image 1 is an IMAGE model, but `calculateTokenUsageAndCost` is called with it to price
  // the PROMPT tokens of an image request (see llms/openai.ts). It needs an LLM-shaped entry to
  // do that. `legacyOnlyInPricingTs` keeps it out of the EngineMeta view so the bespoke literal
  // definition in pricing.ts — three rate keys and a megapixel-scaled meter — stays authoritative.
  [OpenAIImageGenerationModelType.GPT_IMAGE]: {
    input: 5.0 / 1_000_000,
    output: 0,
    cached: undefined,
    name: "GPT Image 1",
    legacyOnlyInPricingTs: true,
  },
  [GroqModelType.LLAMA_3_3_70B_VERSATILE]: {
    input: 1.0000000000000001e-7,
    output: 1.0000000000000001e-7,
    cached: undefined,
    name: "Llama 3.3 70B Versatile",
  },
  [OpenRouterModelType.MINIMAX_M2_7]: { input: 3e-7, output: 0.0000012, cached: undefined, name: "MiniMax M2.7" },
  [ChatGPTReasoningModelType.O1]: { input: 0.000015, output: 0.00006, cached: 0.0000075, name: "OpenAI o1" },
  [ChatGPTReasoningModelType.O3_MINI]: {
    input: 0.0000011,
    output: 0.0000044,
    cached: undefined,
    name: "OpenAI o3-mini",
  },
  [OpenRouterModelType.QWEN_2_5_CODER_32B_INSTRUCT]: {
    input: 3e-7,
    output: 3e-7,
    cached: undefined,
    name: "Qwen 2.5 Coder 32B Instruct",
  },
  [OpenRouterModelType.QWEN_3_235B_A22B]: {
    input: 4.5500000000000004e-7,
    output: 0.0000018200000000000002,
    cached: undefined,
    name: "Qwen 3 235B A22B",
  },
  [OpenRouterModelType.QWEN_3_32B]: { input: 8e-8, output: 2.8e-7, cached: undefined, name: "Qwen 3 32B" },
  [OpenRouterModelType.QWEN_3_CODER_NEXT]: {
    input: 1.1e-7,
    output: 8.000000000000001e-7,
    cached: 7e-8,
    name: "Qwen 3 Coder Next",
  },
  [OpenRouterModelType.QWEN_3_5_122B_A10B]: {
    input: 2.6e-7,
    output: 0.00000208,
    cached: undefined,
    name: "Qwen 3.5 122B A10B",
  },
  [OpenRouterModelType.QWEN_3_5_397B_A17B]: {
    input: 3.85e-7,
    output: 0.0000024500000000000003,
    cached: undefined,
    name: "Qwen 3.5 397B A17B",
  },
  [OpenRouterModelType.GROK_4_3]: {
    input: 0.00000125,
    output: 0.0000025,
    cached: 2.0000000000000002e-7,
    name: "Grok 4.3",
  },
  [OpenRouterModelType.GLM_5_1]: {
    input: 0.0000010500000000000001,
    output: 0.0000035,
    cached: undefined,
    name: "GLM 5.1",
  },
}

const TTS_RATES: Record<string, TtsRate> = {
  [AwsPollyModelType.AWS_POLLY_GENERATIVE]: { perCharacter: 0.00003, name: "AWS Polly Generative" },
  [AwsPollyModelType.AWS_POLLY_LONGFORM]: { perCharacter: 0.0001, name: "AWS Polly Longform" },
  [AwsPollyModelType.AWS_POLLY_NEURAL]: { perCharacter: 0.000016, name: "AWS Polly Neural" },
  [AwsPollyModelType.AWS_POLLY_STANDARD]: { perCharacter: 0.000004, name: "AWS Polly Standard" },
  [ElevenLabsModelType.FLASH_v2_5]: { perCharacter: 0.000059999999999999995, name: "ElevenLabs Flash v2.5" },
  [ElevenLabsModelType.MONOLINGUAL_v1]: { perCharacter: 0.00011999999999999999, name: "ElevenLabs Monolingual" },
  [ElevenLabsModelType.MULTILINGUAL_v2]: { perCharacter: 0.00011999999999999999, name: "ElevenLabs Multilingual" },
  [ElevenLabsModelType.V3]: { perCharacter: 0.00011999999999999999, name: "ElevenLabs v3" },
  [GoogleAudioModelType.GEMINI_3_1_FLASH_TTS]: {
    perCharacter: 0.0000015,
    name: "Gemini 3.1 Flash TTS",
    provider: "replicate",
  },
  [InworldAudioModelType.TTS_1_5_MAX]: { perCharacter: 0.000005, name: "Inworld TTS 1.5 Max", provider: "replicate" },
  [MiniMaxAudioModelType.SPEECH_2_8_TURBO]: {
    perCharacter: 0.000002,
    name: "MiniMax Speech 2.8 Turbo",
    provider: "replicate",
  },
  // Provider rate is the configured one: its live validation run FAILED, so unlike the other
  // TTS models below this number is NOT yet confirmed against a provider-reported cost.
  [MiniMaxAudioModelType.VOICE_CLONING]: {
    perCharacter: 0.000004,
    name: "MiniMax Voice Cloning",
    provider: "replicate",
  },
  [QwenAudioModelType.QWEN3_TTS]: { perCharacter: 0.000001, name: "Qwen 3 TTS", provider: "replicate" },
  [ResembleAIAudioModelType.CHATTERBOX_TURBO]: {
    perCharacter: 0.000006,
    name: "Resemble AI Chatterbox Turbo",
    provider: "replicate",
  },
}

/** Music/audio generation billed per finished output, not per character or per second. */
interface MusicRate {
  perOutput: number
  name: string
  /** Billing host. Every Suno rate below was confirmed against a provider-reported cost. */
  provider?: string
}

/**
 * Suno music models. `modelType` is TTS for historical reasons, but they bill PER OUTPUT — a
 * different basis from ElevenLabs/Polly per-character TTS, which is why they get their own table
 * rather than being forced into `TTS_RATES` (where a per-character shape prices them at $0).
 */
const MUSIC_RATES: Record<string, MusicRate> = {
  [SunoAudioGenerationModelType.SUNO_V4]: { perOutput: 0.06, name: "Suno V4", provider: "kie-ai" },
  [SunoAudioGenerationModelType.SUNO_V4_5]: { perOutput: 0.06, name: "Suno V4.5", provider: "kie-ai" },
  [SunoAudioGenerationModelType.SUNO_V4_5_PLUS]: { perOutput: 0.08, name: "Suno V4.5 Plus", provider: "kie-ai" },
  [SunoAudioGenerationModelType.SUNO_V4_5_ALL]: { perOutput: 0.08, name: "Suno V4.5 All", provider: "kie-ai" },
  [SunoAudioGenerationModelType.SUNO_V5]: { perOutput: 0.1, name: "Suno V5", provider: "kie-ai" },
}

/**
 * An LLM bills three legs at different rates. `rate` carries the output rate so a caller reading
 * it naively still sees the dominant cost driver; `calculateCost` is authoritative and sums all
 * three from the caller's own token counts.
 */
const llmEntry = (r: LlmRate): LlmModelPricing => ({
  rate: r.output,
  unit: EPricingUnit.PER_TOKEN,
  description: r.name,
  calculateCost: (_rate: number, p: LlmPricingParams): number =>
    // `r.cached ?? 0` preserves the pre-migration behaviour exactly: a model with no configured
    // cached-input rate contributes nothing for cached tokens. That is arguably wrong — providers
    // without a cache discount still charge the FULL input rate, so those tokens are currently
    // free in our accounting — but changing it here would be a silent billing change hiding
    // inside a refactor. Tracked as a follow-up; fix it as its own reviewable diff.
    p.inputTokens * r.input +
    p.outputTokens * r.output +
    (p.cachedTokens ?? 0) * (r.cached ?? 0) +
    (p.cacheCreationTokens ?? 0) * r.input * 1.25,
})

/** TTS bills a flat rate per character of synthesized text. */
const ttsEntry = (r: TtsRate): TtsModelPricing => ({
  rate: r.perCharacter,
  unit: EPricingUnit.PER_CHARACTER,
  description: r.name,
  calculateCost: (rate: number, p: TtsPricingParams): number => p.characterCount * rate,
  ...(r.provider
    ? {
        providerCosts: {
          [r.provider]: {
            rate: r.perCharacter,
            unit: EPricingUnit.PER_CHARACTER,
            description: `${r.provider}: $${r.perCharacter}/character (verified live 2026-07-31)`,
          },
        },
      }
    : {}),
})

const mapValues = <T, U>(o: Record<string, T>, f: (v: T) => U): Record<string, U> =>
  Object.fromEntries(Object.entries(o).map(([k, v]) => [k, f(v)]))

/**
 * Per-token legs for one model, so a caller that needs a single leg (e.g. `pricing.ts`'s bespoke
 * GPT-Image entry, which bills prompt tokens at the LLM input rate alongside two image-only rate
 * keys) reads it from THIS table instead of restating the number. One authority per rate.
 */
export const getLlmRate = (modelId: string): { input: number; output: number; cached?: number } | null => {
  const r = LLM_RATES[modelId]
  return r ? { input: r.input, output: r.output, ...(r.cached !== undefined ? { cached: r.cached } : {}) } : null
}

/** Text + web-search models, priced per token. */
export const llmModelPricing: Record<string, LlmModelPricing> = mapValues(LLM_RATES, llmEntry)

/** Text-to-speech models, priced per character. */
export const ttsModelPricing: Record<string, TtsModelPricing> = mapValues(TTS_RATES, ttsEntry)

/** Music generation, priced per finished output. */
const musicEntry = (r: MusicRate): MusicModelPricing => ({
  rate: r.perOutput,
  unit: EPricingUnit.PER_REQUEST,
  description: r.name,
  calculateCost: (rate: number, p: MusicPricingParams): number => p.outputs * rate,
  ...(r.provider
    ? {
        providerCosts: {
          [r.provider]: {
            rate: r.perOutput,
            unit: EPricingUnit.PER_REQUEST,
            description: `${r.provider}: $${r.perOutput}/output (verified live 2026-07-31)`,
          },
        },
      }
    : {}),
})

export const musicModelPricing: Record<string, MusicModelPricing> = mapValues(MUSIC_RATES, musicEntry)

// ═══════════════════════════════════════════════════════════════════════════
//  LEGACY VIEW — what MODEL_METADATA is assembled from
// ═══════════════════════════════════════════════════════════════════════════

const llmMeterSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedTextInput: z.number().int().nonnegative().optional(),
})
const ttsMeterSchema = z.object({ characterCount: z.number().int().nonnegative() })

/**
 * `EngineMeta`-shaped view of the LLM table, so `MODEL_METADATA` can be built by spreading it
 * rather than restating every rate. Typed loosely here to avoid a circular import back into
 * `pricing.ts`, which imports this module.
 */
export const llmEngineMeta: Record<string, unknown> = mapValues(
  Object.fromEntries(Object.entries(LLM_RATES).filter(([, r]) => !r.legacyOnlyInPricingTs)),
  (r) => ({
    displayName: r.name,
    unit: "token" as const,
    ratesUSD: {
      textInput: r.input,
      textOutput: r.output,
      ...(r.cached !== undefined ? { cachedTextInput: r.cached } : {}),
    },
    paramSchema: llmMeterSchema,
    meter: (p: { inputTokens: number; outputTokens: number; cachedTextInput?: number }) => ({
      textInput: p.inputTokens,
      textOutput: p.outputTokens,
      ...(p.cachedTextInput ? { cachedTextInput: p.cachedTextInput } : {}),
    }),
    modelType: "LLM",
  }),
)

/** `EngineMeta`-shaped view of the TTS table. */
export const ttsEngineMeta: Record<string, unknown> = mapValues(TTS_RATES, (r) => ({
  displayName: r.name,
  unit: "character" as const,
  ratesUSD: { textOutput: r.perCharacter },
  paramSchema: ttsMeterSchema,
  meter: (p: { characterCount: number }) => ({ textOutput: p.characterCount }),
  modelType: "TTS",
}))

/** WaveSpeed's `max(3, ...)` floor for InfiniteTalk — a 1.4s clip still bills three seconds. */
export const INFINITETALK_MIN_BILLABLE_SEC = 3
/** WaveSpeed's `min(..., 600)` cap for InfiniteTalk. */
export const INFINITETALK_MAX_BILLABLE_SEC = 600

/**
 * Seconds WaveSpeed actually bills for one `wavespeed-ai/infinitetalk` call.
 *
 * Its published formula is
 * `max(3, min(ceil(get_duration_v3(audio)), 600)) / 5 * base_price * (resolution="720p" ? 2 : 1)`,
 * `base_price = $0.15` — i.e. our $0.03/s (480p) and $0.06/s (720p) rates are already right, but
 * the QUANTITY is whole seconds of the INPUT AUDIO, floored at 3 and capped at 600. We were
 * multiplying the raw, fractional, probed OUTPUT VIDEO duration, which under-recorded every job:
 * a 5.002s output billed $0.3001 against a real `ceil → 6 × $0.06 = $0.36`, and short clips were
 * worse — 1.394s recorded $0.0836 against the 3s floor's $0.18 (2.15x under).
 *
 * InfiniteTalk only. Aurora / OmniHuman / VEED Fabric are fal-hosted and bill plain fractional
 * output seconds, so the rule is attached per rate-table entry rather than to "is lipsync".
 *
 * Non-finite input passes through as NaN so `resolveModelCostUsd` still reports "not priceable"
 * rather than silently billing the 3s floor.
 */
export const billableInfiniteTalkSeconds = (seconds: number): number =>
  Math.min(Math.max(Math.ceil(seconds), INFINITETALK_MIN_BILLABLE_SEC), INFINITETALK_MAX_BILLABLE_SEC)

/**
 * Lipsync / avatar models — image + audio → talking head, billed per output second.
 *
 * Rates vary by submitted resolution (roughly 2x between 480p and 720p), so `calculateCost`
 * resolves the tier rather than letting a caller silently bill the base rate for a 720p render —
 * the defect that had Aurora and VEED Fabric under-recorded by ~2x.
 *
 * Keyed by `ELipsyncModel` values. Provider costs are recorded per host: fal and WaveSpeed both
 * resell several of these at different rates.
 */
const lipsyncEntry = (
  rates: Partial<Record<"480p" | "720p" | "1080p", number>>,
  description: string,
  providerCosts?: LipsyncModelPricing["providerCosts"],
  billableSeconds?: (seconds: number) => number,
): LipsyncModelPricing => {
  // Base = cheapest configured tier, so a caller that omits resolution gets the model's floor
  // rather than an arbitrary pick.
  const base = Math.min(...Object.values(rates).filter((v): v is number => typeof v === "number"))
  return {
    rate: base,
    unit: EPricingUnit.PER_SECOND,
    description,
    calculateCost: (_rate: number, p: LipsyncPricingParams): number => {
      // Providers that round/clamp their billable quantity declare it on their rate-table row;
      // everyone else bills the raw seconds they always did.
      const seconds = billableSeconds ? billableSeconds(p.seconds) : p.seconds
      // No resolution asked for → the model's floor. A resolution that IS asked for but has no
      // configured rate returns NaN, which `resolveModelCostUsd` turns into null via its finite
      // check. Deliberately NOT a guess in either direction: guessing high silently over-bills,
      // guessing low is the exact defect (Aurora and Fabric billed at their 480p rate for 720p
      // renders) this whole resolver exists to prevent. Matches `getRatePerSecAtResolution`,
      // which refuses the same case.
      if (!p.resolution) return seconds * base
      const tier = rates[p.resolution]
      return tier === undefined ? Number.NaN : seconds * tier
    },
    ...(providerCosts ? { providerCosts } : {}),
  }
}

interface LipsyncRate {
  name: string
  /** Per-second rate for each resolution the model actually offers. */
  tiers: Partial<Record<"480p" | "720p" | "1080p", number>>
  description: string
  /**
   * Provider's rounding/clamping rule for the billable quantity, when it does not bill raw
   * fractional seconds. Omitted = raw seconds. Set per model id, never per modality.
   */
  billableSeconds?: (seconds: number) => number
  providerCosts?: LipsyncModelPricing["providerCosts"]
}

/**
 * The one lipsync rate table. Both the `BaseModelPricing` view (`lipsyncModelPricing`) and the
 * legacy `EngineMeta` view (`lipsyncEngineMeta`) are derived from it, so a rate changes in exactly
 * one place — the same shape LLM_RATES / TTS_RATES already use.
 */
const LIPSYNC_RATES: Record<string, LipsyncRate> = {
  [ELipsyncModel.INFINITETALK_480P]: {
    name: "InfiniteTalk 480p",
    tiers: { "480p": 0.03 },
    billableSeconds: billableInfiniteTalkSeconds,
    description: "InfiniteTalk 480p — $0.03/sec (WaveSpeed), whole seconds, min 3s / max 600s",
    providerCosts: { wavespeed: { rate: 0.03, unit: EPricingUnit.PER_SECOND, description: "WaveSpeed: $0.03/sec" } },
  },
  [ELipsyncModel.INFINITETALK_720P]: {
    name: "InfiniteTalk 720p",
    tiers: { "720p": 0.06 },
    billableSeconds: billableInfiniteTalkSeconds,
    description: "InfiniteTalk 720p — $0.06/sec (WaveSpeed), whole seconds, min 3s / max 600s",
    providerCosts: { wavespeed: { rate: 0.06, unit: EPricingUnit.PER_SECOND, description: "WaveSpeed: $0.06/sec" } },
  },
  [ELipsyncModel.AURORA]: {
    name: "Creatify Aurora",
    tiers: { "480p": 0.07, "720p": 0.14 },
    description: "Creatify Aurora — $0.07/sec 480p, $0.14/sec 720p (fal)",
    providerCosts: {
      fal: { rate: 0.14, unit: EPricingUnit.PER_SECOND, description: "fal: $0.14/sec at the 720p we submit" },
    },
  },
  [ELipsyncModel.OMNIHUMAN_V1_5]: {
    name: "OmniHuman V1.5",
    // 720p is OmniHuman's floor (see buildModelInput in providers/fal/lipsyncModels.ts), so there
    // is no 480p tier to differentiate — one rate covers every submission.
    tiers: { "720p": 0.04 },
    description: "OmniHuman v1.5 — $0.04/sec, 720p minimum (fal)",
    providerCosts: { fal: { rate: 0.04, unit: EPricingUnit.PER_SECOND, description: "fal: $0.04/sec" } },
  },
  [ELipsyncModel.VEED_FABRIC]: {
    name: "Veed Fabric 1.0",
    // CORRECTED 2026-07-31 from $0.03/$0.08. The old figures were ~2x below what fal actually
    // charges, and since the UGC path always submits 720p we were recording $0.08/s against a real
    // $0.15/s bill — a negative margin on every Fabric generation. Three sources agree on the fal
    // rate; WaveSpeed resells the same model slightly cheaper, but we route Fabric through fal, so
    // fal's list is our cost basis. `actualCostUsd` is provider cost, not customer price —
    // customers pay a flat 20 credits per UGC generation — so no markup is applied here.
    tiers: { "480p": 0.08, "720p": 0.15 },
    description: "VEED Fabric 1.0 — $0.08/sec 480p, $0.15/sec 720p (fal)",
    providerCosts: {
      fal: { rate: 0.15, unit: EPricingUnit.PER_SECOND, description: "fal: $0.15/sec at the 720p we submit" },
      wavespeed: {
        rate: 0.14,
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed resells at $0.70 per 5s run = $0.14/sec",
      },
    },
  },
}

export const lipsyncModelPricing: Record<string, LipsyncModelPricing> = mapValues(LIPSYNC_RATES, (r) =>
  lipsyncEntry(r.tiers, r.description, r.providerCosts, r.billableSeconds),
)

const quantityMeterSchema = z.object({ quantity: z.number().int().positive() })
const secondsMeterSchema = z.object({ seconds: z.number().nonnegative() })

/**
 * `EngineMeta`-shaped view of the music table.
 *
 * `modelType: "TTS"` and the `imageOutput` rate key are both inherited from how these rows were
 * originally written — Suno bills per finished song, and `imageOutput` was reused as the generic
 * "per output" key. Preserved exactly rather than corrected, because `creditCosts` and the admin UI
 * read those keys; the golden snapshot pins the resolved dollar value either way.
 */
export const musicEngineMeta: Record<string, unknown> = mapValues(MUSIC_RATES, (r) => ({
  displayName: r.name,
  unit: "quantity" as const,
  ratesUSD: { imageOutput: r.perOutput },
  paramSchema: quantityMeterSchema,
  meter: (p: { quantity: number }) => ({ imageOutput: p.quantity }),
  modelType: "TTS",
}))

/**
 * `EngineMeta`-shaped view of the lipsync table.
 *
 * `ratesUSD.secondsOutput` is the model's BASE (lowest configured) tier. Callers that know which
 * resolution they submitted must read `resolutionRatesUSD` via `getRatePerSecAtResolution` — the
 * UGC path always submits 720p, so billing it off the base rate understates Aurora and Fabric ~2x.
 */
// NOTE: this legacy view flattens to the cheapest tier and does NOT apply `billableSeconds` — the
// resolution tiering and InfiniteTalk's ceil/clamp both live on `lipsyncModelPricing`, which is
// what every cost-write path resolves through (`resolveModelCostUsd`). Nothing meters lipsync via
// `MODEL_METADATA`; if something starts to, move the rule here rather than duplicating it.
export const lipsyncEngineMeta: Record<string, unknown> = mapValues(LIPSYNC_RATES, (r) => ({
  displayName: r.name,
  unit: "seconds" as const,
  ratesUSD: { secondsOutput: Math.min(...Object.values(r.tiers).filter((v): v is number => typeof v === "number")) },
  resolutionRatesUSD: r.tiers,
  paramSchema: secondsMeterSchema,
  meter: (p: { seconds: number }) => ({ secondsOutput: p.seconds }),
  modelType: "VIDEO",
}))

// ═══════════════════════════════════════════════════════════════════════════
//  UNIFIED RESOLVER
// ═══════════════════════════════════════════════════════════════════════════

/** Parameters accepted by `resolveModelCostUsd`, discriminated by modality. */
export type ModelPricingParams =
  | ({ kind: "llm" } & LlmPricingParams)
  | ({ kind: "tts" } & TtsPricingParams)
  | ({ kind: "lipsync" } & LipsyncPricingParams)
  | ({ kind: "music" } & MusicPricingParams)

const REGISTRY_BY_KIND = {
  llm: llmModelPricing,
  tts: ttsModelPricing,
  lipsync: lipsyncModelPricing,
  music: musicModelPricing,
} as const

/**
 * Internal USD cost for one generation, resolved from the model's own parameters.
 *
 * This is the single entry point for LLM/TTS/lipsync pricing — callers pass the inputs that
 * actually drive their bill (tokens, characters, seconds+resolution) and never index a rate table
 * by hand. Video and image keep their existing entry points (`calculateVideoCredits` /
 * `getVideoProviderCost`) since those already resolve through `BaseModelPricing` the same way.
 *
 * Returns null for an unregistered model so callers can distinguish "not priced" from "free".
 */
export const resolveModelCostUsd = (
  modelId: string,
  params: ModelPricingParams,
  // Omit for the CONSUMER rate. Pass a provider to price what we PAY: lipsync models declare
  // per-provider rates (InfiniteTalk is $0.03/s at 480p on WaveSpeed vs the canonical rate), and
  // resolving without it recorded the consumer price as the provider cost, zeroing the margin.
  provider?: string,
): number | null => {
  const base = REGISTRY_BY_KIND[params.kind][modelId]
  if (!base) return null
  const providerEntry = provider ? base.providerCosts?.[provider] : undefined
  const entry = (providerEntry ?? base) as typeof base
  const rate = typeof entry.rate === "function" ? (entry.rate as (p: unknown) => number)(params) : entry.rate
  if (typeof rate !== "number" || !Number.isFinite(rate)) return null
  // Provider entries declare only { rate, unit, description }; the units→dollars calculator lives
  // on the base entry. Without this fallback a per-second provider rate returned the bare $0.03
  // instead of $0.03 x duration.
  const calc = (entry.calculateCost ?? base.calculateCost) as ((r: number, p: unknown) => number) | undefined
  if (!calc) return rate
  const out = calc(rate, params)
  return Number.isFinite(out) ? out : null
}
